import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useLogin } from '@/features/auth/hooks/use-login'
import logoSquare from '@/images/logo-square.png'

export const Route = createFileRoute('/_guest/auth/login')({
  component: Login,
  validateSearch: z
    .object({
      redirectTo: z.string().optional(),
    })
    .optional(),
})

const authErrorMessages = {
  invalid_credentials: 'Credenziali non valide',
  over_email_send_rate_limit:
    'Troppe richieste di autenticazione. Riprovare tra poco',
  unexpected_failure:
    "Si è verificato un errore imprevisto. Riprova più tardi o contatta l'assistenza se il problema persiste.",
}

function Login() {
  const navigate = Route.useNavigate()
  const searchParams = Route.useSearch()
  const form = useForm<{ username: string; password: string }>()

  const signInMutation = useLogin({
    onSuccess: () => {
      // Redirect to the intended destination or default to home
      const redirectTo = searchParams?.redirectTo || '/'

      // Validate that redirectTo is a safe internal path
      const isInternalPath =
        redirectTo.startsWith('/') && !redirectTo.startsWith('//')

      if (isInternalPath) {
        // Use window.location to navigate to the full path with search params
        window.location.href = redirectTo
      } else {
        // Fallback to default route
        navigate({ to: '/' })
      }
    },
    onError: (error) => {
      const err = error as Error
      form.setError('username', {
        type: 'custom',
        message:
          // @ts-expect-error I know
          authErrorMessages[err?.code] ??
          err?.message ??
          'Credenziali non valide',
      })
    },
  })

  const onSubmit = () => {
    const values = form.getValues()

    return signInMutation.mutateAsync({
      ...values,
      username: values.username?.trim() ?? '',
    })
  }

  const auth = useAuth()

  if (auth.session) {
    // If already authenticated, redirect to intended destination
    const redirectTo = searchParams?.redirectTo || '/'
    const isInternalPath =
      redirectTo.startsWith('/') && !redirectTo.startsWith('//')

    if (isInternalPath) {
      window.location.href = redirectTo
      return null
    } else {
      return <Navigate to="/" />
    }
  }

  return (
    <>
      <div className="relative container h-svh flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
        <div className="relative hidden h-full flex-col bg-cyan-200 bg-[url('https://picsum.photos/1600/1000/')] bg-cover bg-center p-10 text-white lg:flex dark:border-r">
          {/*<div className="absolute inset-0 bg-zinc-900" />*/}
          {/*<div className="relative z-20 flex items-center text-lg font-medium">*/}
          {/*  <svg*/}
          {/*    xmlns="http://www.w3.org/2000/svg"*/}
          {/*    viewBox="0 0 24 24"*/}
          {/*    fill="none"*/}
          {/*    stroke="currentColor"*/}
          {/*    strokeWidth="2"*/}
          {/*    strokeLinecap="round"*/}
          {/*    strokeLinejoin="round"*/}
          {/*    className="mr-2 size-6"*/}
          {/*  >*/}
          {/*    <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />*/}
          {/*  </svg>*/}
          {/*  Idromeccanica Carfí*/}
          {/*</div>*/}
          {/*<div className="relative z-20 mt-auto">*/}
          {/*  <blockquote className="space-y-2">*/}
          {/*    <p className="text-lg">*/}
          {/*      &ldquo;This library has saved me countless hours of work and*/}
          {/*      helped me deliver stunning designs to my clients faster than*/}
          {/*      ever before.&rdquo;*/}
          {/*    </p>*/}
          {/*    <footer className="text-sm">Sofia Davis</footer>*/}
          {/*  </blockquote>*/}
          {/*</div>*/}
        </div>
        <div className="h-full px-4 md:px-0 lg:p-8">
          <div className="mx-auto flex size-full flex-col justify-center space-y-6 sm:w-[350px]">
            <img
              className="mx-auto max-w-20 object-contain"
              src={logoSquare}
              alt="logo"
            />
            <div className="flex flex-col space-y-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                Idromeccanica Carfí
              </h1>
              <p className="text-muted-foreground text-sm">Effettua il Login</p>
            </div>
            <div className={cn('grid gap-6')}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Username</FormLabel>
                          <FormControl>
                            <Input
                              autoCapitalize="none"
                              autoComplete="username"
                              autoCorrect="off"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormDescription></FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      disabled={
                        signInMutation.isPending ||
                        form.formState.isSubmitSuccessful
                      }
                    >
                      Login
                    </Button>
                  </div>
                </form>
              </Form>
              {searchParams?.redirectTo && (
                <p className="text-muted-foreground text-center text-xs">
                  Ti reindirizzeremo alla pagina richiesta dopo il login
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
